import { useState } from 'react';
import { Codes, SkillsTeams } from './Lists';
import { BusinessHours } from './Hours';
import { Integrations } from './Integrations';
import { IvrFlows } from './Ivr';
import { Licenses } from './Licenses';
import { Canned } from './Canned';
import { Livechat } from './Livechat';
import { Mailboxes } from './Mailboxes';
import { Scorecards } from './Scorecards';
import { Telegram } from './Telegram';
import { WhatsApp } from './WhatsApp';
import { Privacy } from './Privacy';
import { Provider } from './Provider';
import { useNova } from '../../lib/store';
import { Prompts } from './Prompts';
import { Queues } from './Queues';
import { AuditLog, OrgSettings } from './Settings';
import { Carriers, Numbers } from './Telephony';
import { Users } from './Users';

type Section = 'users' | 'skills' | 'queues' | 'ivr' | 'prompts' | 'hours' | 'codes' | 'carriers' | 'numbers' | 'settings' | 'audit' | 'integrations' | 'privacy' | 'licenses' | 'provider' | 'livechat' | 'email' | 'telegram' | 'whatsapp' | 'canned' | 'scorecards';

const GROUPS: { title: string; items: { id: Section; label: string }[] }[] = [
  {
    title: 'People',
    items: [
      { id: 'users', label: 'Users' },
      { id: 'skills', label: 'Skills and teams' },
    ],
  },
  {
    title: 'Routing',
    items: [
      { id: 'queues', label: 'Queues' },
      { id: 'ivr', label: 'IVR flows' },
      { id: 'prompts', label: 'Messages' },
      { id: 'hours', label: 'Business hours' },
      { id: 'codes', label: 'Outcomes and breaks' },
    ],
  },
  {
    title: 'Telephony',
    items: [
      { id: 'carriers', label: 'Carriers' },
      { id: 'numbers', label: 'Numbers' },
    ],
  },
  {
    title: 'Channels',
    items: [
      { id: 'livechat', label: 'Livechat' },
      { id: 'email', label: 'Email' },
      { id: 'whatsapp', label: 'WhatsApp' },
      { id: 'telegram', label: 'Telegram' },
      { id: 'canned', label: 'Canned responses' },
    ],
  },
  {
    title: 'Integrations',
    items: [{ id: 'integrations', label: 'CRM and webhooks' }],
  },
  {
    title: 'This organization',
    items: [
      { id: 'settings', label: 'Settings' },
      { id: 'licenses', label: 'Licenses' },
      { id: 'privacy', label: 'Privacy requests' },
      { id: 'audit', label: 'Audit log' },
    ],
  },
];

/** Admin console: everything an organization admin sets up, without API calls. */
export function AdminView() {
  // The provider's own section, above the organization's: platform admins only
  const platform = useNova((s) => s.user?.role === 'platform_admin');
  const userId = useNova((s) => s.user?.sub ?? '');
  const qaOn = useNova((s) => Boolean(s.features.qa));
  // Quality, when the organization has it
  const withQa = qaOn ? [...GROUPS.slice(0, -2), { title: 'Quality', items: [{ id: 'scorecards' as Section, label: 'Scorecards' }] }, ...GROUPS.slice(-2)] : GROUPS;
  const groups = platform ? [{ title: 'Provider: all companies', items: [{ id: 'provider' as Section, label: 'Organizations' }] }, ...withQa] : withQa;
  // The last section is remembered per person, so signing in as someone else in the same tab starts fresh
  const remembered = `nova.admin.section.${userId}`;
  const [section, setSection] = useState<Section>(() => {
    try {
      return (sessionStorage.getItem(remembered) as Section) || (platform ? 'provider' : 'users');
    } catch {
      return platform ? 'provider' : 'users';
    }
  });
  const go = (s: Section) => {
    setSection(s);
    try {
      sessionStorage.setItem(remembered, s);
    } catch {
      /* fine without */
    }
  };

  return (
    <div className="flex h-full min-h-0 flex-col md:flex-row">
      {/* On wide screens the menu scrolls on its own when it is taller than the window; on phones it is one row that scrolls sideways */}
      <nav aria-label="Admin sections" className="shrink-0 overflow-x-auto border-b border-line bg-hull/40 px-3 py-3 md:w-[220px] md:overflow-y-auto md:overflow-x-hidden md:border-b-0 md:border-r md:py-5">
        <div className="flex gap-1 md:flex-col md:gap-5">
          {groups.map((g) => (
            <div key={g.title} className="flex gap-1 md:flex-col md:gap-0.5">
              <p className="hidden px-3 pb-1 text-xs text-ink-3 md:block">{g.title}</p>
              {g.items.map((it) => (
                <button
                  key={it.id}
                  type="button"
                  onClick={() => go(it.id)}
                  aria-current={section === it.id ? 'page' : undefined}
                  className={`whitespace-nowrap rounded-lg px-3 py-2 text-left text-sm transition ${
                    section === it.id ? 'bg-ion/15 text-ink' : 'text-ink-2 hover:bg-veil/[0.05] hover:text-ink'
                  }`}
                >
                  {it.label}
                </button>
              ))}
            </div>
          ))}
        </div>
      </nav>
      <div className="min-w-0 flex-1 overflow-auto p-4 md:p-6">
        <div className="mx-auto max-w-[1200px]">
          {section === 'users' ? <Users /> : null}
          {section === 'skills' ? <SkillsTeams /> : null}
          {section === 'queues' ? <Queues /> : null}
          {section === 'ivr' ? <IvrFlows /> : null}
          {section === 'prompts' ? <Prompts /> : null}
          {section === 'privacy' ? <Privacy /> : null}
          {section === 'licenses' ? <Licenses /> : null}
          {section === 'livechat' ? <Livechat /> : null}
          {section === 'email' ? <Mailboxes /> : null}
          {section === 'telegram' ? <Telegram /> : null}
          {section === 'whatsapp' ? <WhatsApp /> : null}
          {section === 'canned' ? <Canned /> : null}
          {section === 'scorecards' ? <Scorecards /> : null}
          {section === 'provider' && platform ? <Provider /> : null}
          {section === 'hours' ? <BusinessHours /> : null}
          {section === 'codes' ? <Codes /> : null}
          {section === 'carriers' ? <Carriers /> : null}
          {section === 'numbers' ? <Numbers /> : null}
          {section === 'integrations' ? <Integrations /> : null}
          {section === 'settings' ? <OrgSettings /> : null}
          {section === 'audit' ? <AuditLog /> : null}
        </div>
      </div>
    </div>
  );
}
